import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Crosshair } from "lucide-react";
import { fetchScan, fetchFindings, type Scan, type Finding } from "../lib/api";
import {
  StatusBadge,
  SeverityBadge,
  LoadingState,
  ErrorState,
  EmptyState,
} from "../components/ui";

export default function ScanDetail() {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const [scan, setScan] = useState<Scan | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scanId) return;
    if (scanId === "new" || scanId.length < 10) {
      navigate("/scans", { replace: true });
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([fetchScan(scanId), fetchFindings(scanId)])
      .then(([s, f]) => {
        setScan(s);
        setFindings(Array.isArray(f) ? f : []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"))
      .finally(() => setLoading(false));
  }, [scanId, navigate]);

  if (loading) {
    return <LoadingState label="Loading scan…" />;
  }

  if (error || !scan) {
    return (
      <ErrorState
        message={error || "Scan not found"}
        onRetry={() => navigate("/scans")}
      />
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate("/scans")}
        className="inline-flex items-center gap-1.5 text-sm text-mist-400 transition-colors hover:text-mist-100"
      >
        <ArrowLeft className="h-4 w-4" /> Back to scans
      </button>

      {/* Header */}
      <div className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-mist-400">
              <Crosshair className="h-4 w-4 text-volt-400" />
              <span className="text-xs font-medium uppercase tracking-wider">Target</span>
            </div>
            <h1 className="break-all font-mono text-lg font-medium text-mist-100">{scan.target}</h1>
          </div>
          <StatusBadge status={scan.status} />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4 sm:grid-cols-4">
          <div>
            <p className="text-xs text-mist-500">Mode</p>
            <p className="mt-0.5 capitalize text-mist-200">{scan.mode}</p>
          </div>
          <div>
            <p className="text-xs text-mist-500">Findings</p>
            <p className="mt-0.5 tabular-nums text-mist-200">{scan.findings_count}</p>
          </div>
          <div>
            <p className="text-xs text-mist-500">Started</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-mist-200">
              <Clock className="h-3.5 w-3.5 text-mist-500" />
              {new Date(scan.created_at).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-mist-500">Scan ID</p>
            <p className="mt-0.5 truncate font-mono text-xs text-mist-400">{scan.id}</p>
          </div>
        </div>
      </div>

      {/* Findings */}
      <section>
        <h2 className="mb-3 font-display text-base font-semibold tracking-tight text-mist-100">
          Findings <span className="text-mist-500">({findings.length})</span>
        </h2>

        {findings.length === 0 ? (
          <EmptyState
            title="No findings yet"
            description={
              scan.status === "running"
                ? "The scan is still in progress — findings will appear here as agents confirm them."
                : "This scan completed without any confirmed vulnerabilities."
            }
          />
        ) : (
          <div className="space-y-3">
            {findings.map((f) => (
              <div key={f.id} className="card group p-5 transition-colors hover:border-line-strong">
                <div className="mb-2 flex items-center gap-2">
                  <SeverityBadge severity={f.severity} />
                  {f.cvss_score != null && (
                    <span className="font-mono text-xs text-mist-500">CVSS {f.cvss_score}</span>
                  )}
                </div>
                <h3 className="font-medium text-mist-100">{f.title}</h3>
                {f.endpoint && (
                  <p className="mt-1 break-all font-mono text-xs text-mist-400">{f.endpoint}</p>
                )}
                {f.description && (
                  <p className="mt-2 line-clamp-2 text-sm text-mist-400">{f.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
